import React, { useCallback, useState } from 'react';
import { Box, Spinner, Text } from '@chakra-ui/react';
import CompanySelector from '../../GlobalComponent/companySelector/CompanySelector';
import { getCompanyIndexesByCompany } from '../../Http/Index';

// ─── Types ───────────────────────────────────────────────────────────────────

interface IndexEntry {
  ambulance_Index: number;
  data_Index: number;
  hospital_Index: number;
  overall_Index: number;
  beneficiaryCount: number;
  updatedAt: string;
}

interface RelationshipEntry {
  data_Index: number;
  hospital_Index: number;
  ambulance_Index: number;
  overall_Index: number;
  beneficiaryCount: number;
}

interface RelationshipRow {
  relationship: string;
  overall: RelationshipEntry;
  activated: RelationshipEntry | null;
  non_activated: RelationshipEntry | null;
}

interface CompanyIndexResponse {
  companyId: string;
  overall: IndexEntry | null;
  byType: (IndexEntry & { ben_type: string })[];
  byRelationship: RelationshipRow[];
}

type Section = 'overall' | 'activated' | 'non_activated';

// ─── Constants ───────────────────────────────────────────────────────────────

const INDEX_KEYS: (keyof Omit<IndexEntry, 'beneficiaryCount' | 'updatedAt'>)[] = [
  'ambulance_Index', 'data_Index', 'hospital_Index', 'overall_Index',
];

const SECTIONS: Section[] = ['overall', 'activated', 'non_activated'];

const SECTION_LABELS: Record<Section, string> = {
  overall: 'Overall',
  activated: 'Activated',
  non_activated: 'Non Activated',
};

const EMPTY_MESSAGES: Record<Section, string> = {
  overall: 'No Overall Data Available',
  activated: 'No Activated Beneficiary Data Available',
  non_activated: 'No Non-Activated Beneficiary Data Available',
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

const getIndexColor = (value: number): string => {
  if (value < 30) return '#ff4444';
  if (value <= 75) return '#ffaa00';
  return '#44aa44';
};

const formatIST = (iso: string): string => {
  const opts: Intl.DateTimeFormatOptions = { timeZone: 'Asia/Kolkata' };
  return `${new Date(iso).toLocaleDateString('en-IN', opts)} ${new Date(iso).toLocaleTimeString('en-IN', opts)}`;
};

const formatLabel = (key: string): string =>
  key.replace('_Index', '').replace('_', ' ');

const COLOR_LEGEND = [
  { color: '#ff4444', label: 'Low', range: '< 30%' },
  { color: '#ffaa00', label: 'Medium', range: '30% – 75%' },
  { color: '#44aa44', label: 'High', range: '> 75%' },
];

const ColorLegend: React.FC = () => (
  <Box display="flex" gap={4} alignItems="center" p={3} border="1px solid #ddd" borderRadius="md" bg="gray.50">
    <Text fontSize="sm" fontWeight="bold" color="gray.600">Index Score:</Text>
    {COLOR_LEGEND.map(({ color, label, range }) => (
      <Box key={label} display="flex" alignItems="center" gap={2}>
        <Box w={3} h={3} borderRadius="sm" bg={color} />
        <Text fontSize="sm" color="gray.700"><b>{label}</b> {range}</Text>
      </Box>
    ))}
  </Box>
);

const RelationshipTable: React.FC<{ title: string; rows: RelationshipRow[]; field: 'overall' | 'activated' | 'non_activated' }> = ({ title, rows, field }) => (
  <Box mt={6}>
    <Text fontSize="md" fontWeight="bold" mb={2}>{title}</Text>
    <Box overflowX="auto">
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
        <thead>
          <tr style={{ background: '#f0f0f0' }}>
            {['Sl. No', 'Relationship', 'Data Index', 'Hospital Index', 'Ambulance Index', 'Overall Index', 'Count'].map((h) => (
              <th key={h} style={{ border: '1px solid #ddd', padding: '8px 12px', textAlign: 'left' }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => {
            const entry = row[field];
            if (!entry) return (
              <tr key={row.relationship}>
                <td style={{ border: '1px solid #ddd', padding: '8px 12px' }}>{i + 1}</td>
                <td style={{ border: '1px solid #ddd', padding: '8px 12px' }}>{row.relationship}</td>
                <td colSpan={5} style={{ border: '1px solid #ddd', padding: '8px 12px', color: '#aaa', textAlign: 'center' }}>N/A</td>
              </tr>
            );
            return (
              <tr key={row.relationship}>
                <td style={{ border: '1px solid #ddd', padding: '8px 12px' }}>{i + 1}</td>
                <td style={{ border: '1px solid #ddd', padding: '8px 12px' }}>{row.relationship}</td>
                {(['data_Index', 'hospital_Index', 'ambulance_Index', 'overall_Index'] as const).map((k) => (
                  <td key={k} style={{ border: '1px solid #ddd', padding: '8px 12px', color: getIndexColor(entry[k]), fontWeight: 'bold' }}>{entry[k]}</td>
                ))}
                <td style={{ border: '1px solid #ddd', padding: '8px 12px' }}>{entry.beneficiaryCount}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </Box>
  </Box>
);

// ─── Sub-components ──────────────────────────────────────────────────────────

const EmptyCard: React.FC<{ section: Section }> = ({ section }) => (
  <Box p={4} border="1px solid #ddd" borderRadius="md" display="flex" alignItems="center" justifyContent="center" minH="120px">
    <Text color="gray.400" fontSize="md" fontWeight="medium">{EMPTY_MESSAGES[section]}</Text>
  </Box>
);

const IndexCard: React.FC<{ section: Section; entry: IndexEntry }> = ({ section, entry }) => (
  <Box p={4} border="1px solid #ddd" borderRadius="md">
    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
      <Text fontWeight="bold" fontSize="md">{SECTION_LABELS[section]}</Text>
      <Text fontSize="sm" color="gray.500">👥 {entry.beneficiaryCount} Beneficiaries</Text>
    </Box>

    {entry.updatedAt && (
      <Box bg="blue.50" border="1px solid" borderColor="blue.200" borderRadius="md" px={3} py={2} mb={3}>
        <Text fontSize="xs" color="blue.700" fontWeight="medium">📅 {formatIST(entry.updatedAt)}</Text>
      </Box>
    )}

    <Box display="grid" gridTemplateColumns="repeat(2, 1fr)" gap={3}>
      {INDEX_KEYS.map((key) => (
        <Box key={key} p={3} borderRadius="md" bg={getIndexColor(entry[key])}>
          <Text fontWeight="bold" color="white" fontSize="sm">{formatLabel(key)} Index:</Text>
          <Text fontWeight="bold" fontSize="lg" color="white">{entry[key]}</Text>
        </Box>
      ))}
    </Box>
  </Box>
);

// ─── Main Component ───────────────────────────────────────────────────────────

const CompanyIndex: React.FC = () => {
  const [companySelected, setCompanySelected] = useState('');
  const [loading, setLoading] = useState(false);
  const [indexResponse, setIndexResponse] = useState<CompanyIndexResponse | null>(null);
  const [hasSearched, setHasSearched] = useState(false);

  const fetchCompanyIndex = useCallback(async (companyId: string) => {
    setLoading(true);
    try {
      const { data } = await getCompanyIndexesByCompany(companyId);
      setIndexResponse(data);
    } catch (error) {
      console.error('Error fetching company index:', error);
      setIndexResponse(null);
    } finally {
      setLoading(false);
      setHasSearched(true);
    }
  }, []);

  const handleCompanySelect = useCallback((companyId: string, companyName: string) => {
    setCompanySelected(companyName);
    setHasSearched(false);
    fetchCompanyIndex(companyId);
  }, [fetchCompanyIndex]);

  const getEntry = (section: Section): IndexEntry | undefined => {
    if (section === 'overall') return indexResponse?.overall ?? undefined;
    return indexResponse?.byType.find((d) => d.ben_type === section);
  };

  const hasData = !!(indexResponse?.overall || indexResponse?.byType?.length || indexResponse?.byRelationship?.length);

  return (
    <Box p={4}>
      <Box display="flex" alignItems="flex-end" gap={4} mb={6}>
        <CompanySelector selectedCompany={companySelected} onCompanySelect={handleCompanySelect} />
        <ColorLegend />
      </Box>

      {loading && (
        <Box display="flex" justifyContent="center" mt={4}>
          <Spinner size="lg" />
        </Box>
      )}

      {hasSearched && !loading && (
        <Box mt={6}>
          {hasData ? (
            <>
              <Text fontSize="lg" fontWeight="bold" mb={4}>Company Index for {companySelected}</Text>
              <Box display="grid" gridTemplateColumns="repeat(3, 1fr)" gap={6}>
                {SECTIONS.map((section) => {
                  const entry = getEntry(section);
                  return entry
                    ? <IndexCard key={section} section={section} entry={entry} />
                    : <EmptyCard key={section} section={section} />;
                })}
              </Box>
              {indexResponse?.byRelationship?.length ? (
                <>
                  <RelationshipTable title="Overall — By Relationship" rows={indexResponse.byRelationship} field="overall" />
                  <RelationshipTable title="Activated — By Relationship" rows={indexResponse.byRelationship} field="activated" />
                  <RelationshipTable title="Non Activated — By Relationship" rows={indexResponse.byRelationship} field="non_activated" />
                </>
              ) : null}
            </>
          ) : (
            <Box p={4} border="1px solid #ddd" borderRadius="md" textAlign="center" py={8}>
              <Text fontSize="lg" color="gray.500">No data available for {companySelected}</Text>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default CompanyIndex;
